import type { PermissionKey } from '@pcrm/shared/access';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { expireSession } from './session-expiry';
import {
  type ActiveCompany,
  pingSession,
  SESSION_KEEP_ALIVE_MS,
  type Session,
  sessionQueryOptions,
  type SessionUser,
} from './session';

/**
 * Operatorul curent, în rutele de sub `_authenticated`.
 *
 * Aruncă dacă nu există sesiune — și e corect să arunce: `beforeLoad`-ul stratului
 * `_authenticated` a redirecționat deja spre `/login`, deci codul care ajunge aici fără
 * sesiune e o rută pusă în locul greșit, nu un utilizator nelogat.
 */
export function useSession(): Session {
  const { data } = useSuspenseQuery(sessionQueryOptions());

  if (!data) {
    throw new Error('useSession folosit în afara stratului autentificat.');
  }

  return data;
}

export function useCurrentUser(): SessionUser {
  return useSession().user;
}

/**
 * Firma pe care lucrează sesiunea. `null` = niciuna aleasă.
 *
 * Ecranele care depind de firmă o citesc de aici, nu dintr-un context propriu: sursa e
 * răspunsul serverului, iar o a doua copie ar putea rămâne în urmă după schimbarea firmei.
 */
export function useActiveCompany(): ActiveCompany | null {
  return useSession().activeCompany;
}

/**
 * Verificarea de permisiune pentru UI.
 *
 * ⚠️ **E doar UX.** Ascunde butoane pe care operatorul n-are ce face; nu protejează
 * nimic. Granița de securitate e `PermissionsGuard` de pe server, care nu se uită la ce
 * afișează pagina. Orice ecran ascuns aici trebuie să aibă și `@RequirePermission` pe
 * endpoint-ul din spate.
 */
export function useHasPermission(): (permission: PermissionKey) => boolean {
  const user = useCurrentUser();
  return (permission) => user.permissions.includes(permission);
}

/**
 * Ține sesiunea vie cât timp aplicația e pe ecran, și duce la login când nu mai e.
 *
 * Se montează **o dată**, în cadrul autentificat. Două lucruri, amândouă necesare:
 *
 * - **prelungirea** — un ping periodic mută `last_seen_at` înainte, deci fereastra de
 *   inactivitate nu se închide sub o filă lăsată deschisă. Numai când fila e *vizibilă*:
 *   un tab uitat de trei zile într-un browser deschis nu e „operator activ", iar o sesiune
 *   ținută vie de un timer ar goli de sens tocmai fereastra pe care o prelungește.
 * - **ieșirea** — dacă serverul spune că sesiunea nu mai există, se merge la login imediat,
 *   nu la prima salvare eșuată. Altfel operatorul completează un formular care oricum va
 *   pica.
 *
 * ⚠️ **Durata absolută nu se prelungește.** `SESSION_ABSOLUTE_HOURS` (30 de zile) rămâne
 * plafonul: la capătul ei ping-ul primește 401 și operatorul se re-autentifică. Un
 * heartbeat care ar împinge și plafonul ar transforma sesiunea într-una fără sfârșit —
 * vezi decizia din architecture/auth.md#ciclul-de-viață-al-sesiunii.
 */
export function useSessionKeepAlive(): void {
  useEffect(() => {
    let cancelled = false;

    const renew = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      void pingSession()
        .then((alive) => {
          if (!cancelled && !alive) {
            expireSession();
          }
        })
        // Înghițit deliberat: o pană de rețea nu e o sesiune expirată, iar `fetchSession`
        // transformă în `null` **doar** un 401 curat. Cine e offline rămâne pe ecran și
        // reîncearcă la următorul ping.
        .catch(() => undefined);
    };

    // Și la revenirea pe filă, nu doar pe cronometru: timer-ele browserului sunt
    // încetinite în filele de fundal, deci ping-ul care „trebuia" să plece acum o oră
    // poate să nu fi plecat.
    document.addEventListener('visibilitychange', renew);
    const timer = window.setInterval(renew, SESSION_KEEP_ALIVE_MS);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', renew);
      window.clearInterval(timer);
    };
  }, []);
}
